import { forwardRef, type ChangeEvent } from 'react'

export interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string
  error?: string
  onChange?: (value: string) => void
}

const formatPhoneNumber = (value: string): string => {
  const numbers = value.replace(/\D/g, '')

  if (numbers.length === 0) return ''
  if (numbers.length <= 3) return `(${numbers}`
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`
}

const unformatPhoneNumber = (value: string): string => {
  return value.replace(/\D/g, '').slice(0, 10)
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ label, error, className = '', onChange, value, disabled, ...props }, ref) => {
    const hasError = !!error
    const formattedValue = value ? formatPhoneNumber(String(value)) : ''

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const rawValue = unformatPhoneNumber(e.target.value)
      onChange?.(rawValue)
    }

    return (
      <div className="flex flex-col mb-5 w-full">
        {label && (
          <label className={`enrollment-field-label ${hasError ? '!text-error' : ''}`}>
            {label}
          </label>
        )}
        <input
          ref={ref}
          type="tel"
          disabled={disabled}
          value={formattedValue}
          onChange={handleChange}
          placeholder="(555) 123-4567"
          maxLength={14}
          className={`enrollment-field-control ${hasError ? 'enrollment-field-control--error' : ''} ${className}`}
          {...props}
        />
        {error && <span className="text-xs text-error mt-1.5 flex items-center gap-1">{error}</span>}
      </div>
    )
  }
)

PhoneInput.displayName = 'PhoneInput'
